<template>
  <div>
    <!-- form -->
    <validation-observer
      ref="form"
      #default="{invalid}"
    >
      <b-form
        class="auth-login-form"
        @submit.prevent="loginOrRegister"
      >
        <!-- forgot password -->
        <b-form-group>
          <b-row class="password-information">
            <b-col sm="12">
              <h3 class="otp-heading">
                {{ $t('auth.password.information.description.' + (authType === 'phone' ? 'phone' : 'email')) }}
              </h3>
            </b-col>

            <b-col sm="12" class="mt-2">
              <div class="d-flex justify-content-center">
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Password')"
                  vid="password"
                >
                  <PincodeInput
                    v-model="password"
                  />
                  <br>
                  <div class="text-danger text-center mt-2">{{ errors[0] }}</div>
                </validation-provider>
              </div>
            </b-col>

            <b-col sm="12">
              <div class="resend-wrapper">
                <span class="resend-hint">{{ $t('auth.password.Didn\'t receive the code') }}</span>
                <button
                  type="button"
                  class="resend-button"
                  :class="{ 'is-disabled': !sendAgainable }"
                  :disabled="!sendAgainable || loading"
                  @click="sendAgain"
                >
                  <feather-icon
                    v-if="sendAgainable"
                    icon="RefreshCwIcon"
                    size="14"
                    class="resend-icon"
                  />
                  <span v-if="sendAgainable">{{ $t('auth.password.Resend Pin Code') }}</span>
                  <span v-else class="resend-countdown">{{ formattedCountdown }}</span>
                </button>
              </div>
            </b-col>
          </b-row>
        </b-form-group>

        <!-- submit buttons -->
        <b-button
          class="mt-2"
          type="submit"
          variant="primary"
          block
          :disabled="loading || invalid"
        >
          <b-spinner v-if="loading" small />
          {{ $t('Continue') }}
        </b-button>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { takeLoginRedirect } from '@/auth/utils'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormGroup, BForm, BButton, BSpinner, BCol, BRow,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
// eslint-disable-next-line import/extensions
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import PincodeInput from 'vue-pincode-input'
import {
  clarityPushUserId,
  gtmPushUserId,
  gtmTrackNewEvent,
  throwDefaultError,
} from '@/helpers/helpers'
import { logEvent } from 'firebase/analytics'

const defaultAbility = [
  {
    action: 'manage',
    subject: 'all',
  },
  {
    action: 'read',
    subject: 'all',
  },
]

export default {
  components: {
    BCol,
    BForm,
    BButton,
    BFormGroup,
    BRow,
    BSpinner,

    PincodeInput,

    ValidationObserver,
    ValidationProvider,
  },
  mixins: [togglePasswordVisibility],
  props: {
    backButton: {
      type: String,
      default: '',
      required: false,
    },
    email: {
      type: String,
      default: '',
      required: false,
    },
    phone: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      loading: false,
      password: '',
      sendAgainCooldown: 60,
      sendAgainCountdown: 60,
      sendAgainTimer: null,
    }
  },
  computed: {
    authType() {
      return this.$store.getters['auth/authType']
    },
    user() {
      return this.$store.getters['auth/tempUser2']
    },
    sendAgainable() {
      return this.sendAgainCountdown <= 0
    },
    formattedCountdown() {
      const total = Math.max(this.sendAgainCountdown, 0)
      const m = Math.floor(total / 60)
      const s = total % 60
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
  },
  watch: {
    password(newValue, oldValue) {
      if (oldValue.length === 3 && newValue.length === 4) {
        this.loginOrRegister()
      }
    },
  },
  activated() {
    this.startResendCooldown()
  },
  created() {
    this.startResendCooldown()
  },
  beforeDestroy() {
    if (this.sendAgainTimer) {
      clearInterval(this.sendAgainTimer)
      this.sendAgainTimer = null
    }
  },
  methods: {
    startResendCooldown() {
      if (this.sendAgainTimer) {
        clearInterval(this.sendAgainTimer)
      }
      this.sendAgainCountdown = this.sendAgainCooldown
      this.sendAgainTimer = setInterval(() => {
        if (this.sendAgainCountdown > 0) {
          this.sendAgainCountdown -= 1
        } else {
          clearInterval(this.sendAgainTimer)
          this.sendAgainTimer = null
        }
      }, 1000)
    },
    letIn(response) {
      useJwt.setToken(response.data.body.loginToken.token)
      this.$store.dispatch('auth/setToken', response.data.body.loginToken.token)
      this.$store.dispatch('auth/updateUser', {
        ability: defaultAbility,
        ...response.data.body,
      })
      clarityPushUserId(response.data.body.id)
      gtmPushUserId(response.data.body.id)
      gtmTrackNewEvent('login', {
        category: 'Login Funnel',
        action: 'Login',
      })
      this.$ability.update(defaultAbility)
      this.$router.push(takeLoginRedirect() || '/')
    },
    loginOrRegister() {
      this.$emit('updatePassword', this.password)
      if (this.$store.getters['auth/tempUser2']) {
        this.register()
      } else {
        console.log('login')
        this.login()
      }
    },
    // eslint-disable-next-line consistent-return
    login() {
      if (this.password.length < 4) {
        this.$refs.form.setErrors({
          password: this.$t('{field} is required', { field: this.$t('Password') }),
        })
        return false
      } else {
        // Email mi telefon mu
        if (this.authType === 'email') {
          if (this.email && this.email !== '') {
            this.loading = true
            useJwt.emailLogin({
              email: this.email,
              pinCode: this.password,
              lang: this.$i18n.locale,
              deviceId: this.$store.getters['app/deviceId'],
            })
              .then(response => {
                this.letIn(response)
              })
              .catch(error => {
                throwDefaultError(this.$toast, error)
                // this.$toast({
                //   component: ToastificationContent,
                //   props: {
                //     title: 'Giriş bilgilerini kontrol edip tekrar denemelisin',
                //     variant: 'danger',
                //   },
                // })
              })
              .finally(() => {
                this.loading = false
              })
          }
        } else if (this.phone && this.phone !== '') {
          this.loading = true
          useJwt.login({
            phoneNumber: this.phone,
            password: this.password,
            lang: this.$i18n.locale,
          })
            .then(response => {
              this.letIn(response)
            })
            .catch(error => {
              throwDefaultError(this.$toast, error)
              // this.$toast({
              //   component: ToastificationContent,
              //   props: {
              //     title: 'Giriş bilgilerini kontrol edip tekrar denemelisin',
              //     variant: 'danger',
              //   },
              // })
            })
            .finally(() => {
              this.loading = false
            })
        }
      }
    },
    register() {
      if (this.password.length < 4) {
        this.$refs.form.setErrors({
          password: this.$t('{field} is required', { field: this.$t('Password') }),
        })
        return false
      } else {
        this.loading = true
        if (this.authType === 'email') {
          useJwt.emailRegister({
            email: this.email,
            pinCode: this.password,
            lang: this.$i18n.locale,
            deviceId: this.$store.getters['app/deviceId'],
          })
            .then(async response => {
              console.log('emailRegister response', response)
              gtag('event', 'conversion', {
                send_to: 'AW-805828651/lOrECPO8oYIBEMPwn4AD',
                user: response.data.body,
              })

              // Firebase Analytics Event
              if (window.firebaseAnalytics) {
                logEvent(window.firebaseAnalytics, 'web_registration_success', response.data.body)
              }

              useJwt.setToken(response.data.body.loginToken.token)
              await this.$store.dispatch('auth/setToken', response.data.body.loginToken.token)
              await this.$store.dispatch('auth/updateUser', {
                ability: defaultAbility,
                ...response.data.body,
              })

              this.$emit('updateActiveComponent', 'PersonalInformation')
              this.$emit('updateBackButton', 'Password')

              // const data = {
              //   userName: this.user.userName,
              // }

              // const profile = {
              //   gender: this.user.gender,
              //   country: this.user.country,
              //   lookingForGender: this.user.lookingForGender,
              //   userName: this.user.userName,
              //   date: this.user.profile.date,
              //   month: this.user.profile.month,
              //   year: this.user.profile.year,
              // }
              //
              // this.loading = true
              //
              // useJwt.putPendingUser(profile)
              //   .then(() => {
              //     this.$ability.update(defaultAbility)
              //     if (localStorage.getItem('loginRedirect')) {
              //       this.$router.push({ name: localStorage.getItem('loginRedirect') })
              //       localStorage.removeItem('loginRedirect')
              //     } else {
              //       this.$router.push('/')
              //     }
              //   })
              //   .catch(error => {
              //     throwDefaultError(this.$toast, error)
              //     this.loading = false
              //   })

              // const _updateMe = useJwt.updateMe(data)
              // const _updateProfile = useJwt.updateProfile(profile)
              //
              // Promise.all([_updateMe, _updateProfile])
              //     .then(values => {
              //       this.$ability.update(defaultAbility)
              //       this.$router.push('/')
              //     })
              //     .catch(error => {
              //       throwDefaultError(this.$toast, error)
              //     })
              //     .finally(() => { this.loading = false })
            })
            .catch(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('You should check your login information and try again'),
                  variant: 'danger',
                },
              })
              this.loading = false
            })
        } else {
          this.$emit('updateActiveComponent', 'PersonalInformation')
          this.$emit('updateBackButton', 'Password')

          const userData = {
            /// ...this.user,
            country: this.user.country,
            gender: this.user.gender,
            lookingForGender: this.user.lookingForGender,
            userName: this.user.userName,
            phoneNumber: this.phone,
            password: this.password,
            date: this.user.profile.date,
            month: this.user.profile.month.value,
            year: this.user.profile.year,
            lang: this.$i18n.locale,
            deviceId: this.$store.getters['app/deviceId'],
          }
          useJwt.register(userData)
            .then(response => {
              gtag('event', 'conversion', {
                send_to: 'AW-805828651/lOrECPO8oYIBEMPwn4AD',
                user: response.data.body,
              })

              useJwt.setToken(response.data.body.loginToken.token)
              this.$store.dispatch('auth/setToken', response.data.body.loginToken.token)
              useJwt.claimStoredReferral()
              this.$store.dispatch('auth/updateUser', {
                ability: defaultAbility,
                ...response.data.body,
              })
              this.$ability.update(defaultAbility)
              this.$router.push(takeLoginRedirect() || '/')
            })
            .catch(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('You should check your login information and try again'),
                  variant: 'danger',
                },
              })
            })
            .finally(() => {
              this.loading = false
            })
        }
      }
    },
    sendAgain() {
      this.loading = true
      const request = this.authType === 'email'
        ? useJwt.forgotPasswordEmail({ email: this.email, lang: this.$i18n.locale })
        : useJwt.forgotPassword(this.phone, this.$i18n.locale)

      request
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.authType === 'email'
                ? this.$t('Password sent to email address.')
                : this.$t('Password sent to phone number.'),
              variant: 'success',
            },
          })
          this.startResendCooldown()
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.password-information {
  font-family: 'Lexend', sans-serif;

  .otp-heading {
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 26px;
    text-align: center;
    color: #000000;
    max-width: 420px;
    margin: 0 auto;
  }

  .resend-wrapper {
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    font-family: inherit;

    .resend-hint {
      font-weight: 300;
      font-size: 14px;
      line-height: 20px;
      color: #8893A4;
    }

    .resend-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-width: 168px;
      height: 38px;
      padding: 0 18px;
      background: transparent;
      border: 1px solid #F9447A;
      border-radius: 999px;
      color: #F9447A;
      font-weight: 500;
      font-size: 14px;
      line-height: 1;
      cursor: pointer;
      transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.1s ease;

      .resend-icon {
        transition: transform 0.4s ease;
      }

      &:hover:not(:disabled) {
        background: #F9447A;
        color: #fff;

        .resend-icon {
          transform: rotate(180deg);
        }
      }

      &:active:not(:disabled) {
        transform: scale(0.98);
      }

      &:disabled,
      &.is-disabled {
        cursor: not-allowed;
        background: #F5F6F8;
        border-color: #E4E7EC;
        color: #8893A4;
      }

      .resend-countdown {
        font-variant-numeric: tabular-nums;
        letter-spacing: 0.5px;
      }
    }
  }
}
</style>

<style lang="scss">
div.vue-pincode-input-wrapper {
  .vue-pincode-input {
    margin: 0 8px;
    padding: 5px;
    text-align: center;
    border: none;
    box-shadow: 0 0 2px rgb(0 0 0 / 50%);
    border-radius: 12px;
    font-family: 'Lexend', sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 28px;
    line-height: 35px;
    color: #000000;

    &:focus {
      box-shadow: none;
      border: 2px solid #000;
    }
  }
}
</style>
